import { normalizeFusionResultCode } from "./fusionResult";
import { toTimeseriesNumeric } from "pages/files/image/report/timeseries/utils/timeseriesChart";

export const RESULT_LABELS = ["정상", "요주의", "이상", "재점검 요망"];
export const numeric = toTimeseriesNumeric;

// Capture timestamps come from the device filename (KST); upload timestamps are UTC.
function parseTime(value, offset = "Z") {
  if (!value) return null;
  const text = String(value).trim().replace(" ", "T");
  if (!/^\d{4}-\d{2}-\d{2}/.test(text) || text.startsWith("0000")) return null;
  const [year, month, day] = text.slice(0, 10).split("-").map(Number);
  const calendar = new Date(Date.UTC(year, month - 1, day));
  if (calendar.getUTCFullYear() !== year || calendar.getUTCMonth() !== month - 1 || calendar.getUTCDate() !== day) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T00:00:00+09:00`
    : /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : `${text}${offset}`;
  const time = Date.parse(normalized);
  return Number.isFinite(time) ? time : null;
}

export function recordTime(file) {
  const shot = parseTime(file.shooting_date, "+09:00");
  if (shot != null) return { time: shot, source: "shooting" };
  return { time: parseTime(file.uploaded_at), source: "uploaded" };
}

export function dateLabel(file) {
  if (!file) return "측정 이력 없음";
  const { time, source } = recordTime(file);
  if (time == null) return "날짜 미상";
  return `${new Date(time).toLocaleString("ko-KR", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}${source === "uploaded" ? " · 등록일 기준" : ""}`;
}

export function sortRecords(files) {
  return [...files].sort((a, b) => (recordTime(a).time ?? -Infinity) - (recordTime(b).time ?? -Infinity)
    || (parseTime(a.uploaded_at) ?? 0) - (parseTime(b.uploaded_at) ?? 0)
    || String(a.upload_id).localeCompare(String(b.upload_id)));
}

export function resultCode(file) {
  return file && String(file.fusion_result_code ?? "").trim() ? normalizeFusionResultCode(file.fusion_result_code) : null;
}

export function resultLabel(file) {
  return file ? RESULT_LABELS[resultCode(file)] || "판정 없음" : "측정 이력 없음";
}

export function pointValue(file, index) {
  return numeric(file.click_points?.[index]?.value);
}

// Same definition as the existing QR history: maximum minus minimum valid spot.
export function pointDifference(file) {
  const values = (file.click_points || []).map(point => numeric(point?.value)).filter(value => value != null);
  return values.length >= 2 ? Math.max(...values) - Math.min(...values) : null;
}

export const METRICS = [
  { id: "center", label: "센터 온도", unit: "°C", color: "#b45309", value: file => numeric(file.thermal_center) },
  ...[0, 1, 2].map(index => ({ id: `point${index + 1}`, label: `지점 ${index + 1}`, unit: "°C", color: ["#2563eb", "#0f766e", "#7c3aed"][index], value: file => pointValue(file, index) })),
  { id: "difference", label: "3점 온도차 (최대−최소)", unit: "°C", color: "#475569", value: pointDifference },
  { id: "ultrasound", label: "평균 초음파", unit: "dB", color: "#0369a1", value: file => numeric(file.avg_decibel) },
];

export function metricText(value, unit = "") {
  return value == null ? "미분석" : `${value.toLocaleString("ko-KR", { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ""}`;
}

export function previousChange(records, current, metric) {
  const time = recordTime(current).time;
  const value = metric.value(current);
  if (time == null || value == null) return null;
  const previous = [...records].reverse().find(file => recordTime(file).time != null && recordTime(file).time < time && metric.value(file) != null);
  return previous ? { value: value - metric.value(previous), previous } : null;
}

export function fileHref(projectId, file) {
  return ["image", "video"].includes(file?.file_type)
    ? `/project/${encodeURIComponent(projectId)}/file_${file.file_type}/${encodeURIComponent(file.upload_id)}` : null;
}
